import { ReactiveController, ReactiveControllerHost } from "lit";
import { AppState, RatingConfig } from "../models";
import { encodeRatings, decodeRatings } from "../utils/serialization";
import { saveToLocal, loadFromLocal } from "../utils/storage";

/**
 * Controller for managing AppState persistence (URL hash & LocalStorage).
 */
export class PersistenceController implements ReactiveController {
  state!: AppState;
  externalHashChange = false;

  private _stateLoaded = false;

  constructor(
    private host: ReactiveControllerHost,
    private _getConfig: () => RatingConfig
  ) {
    host.addController(this);
  }

  hostConnected() {
    if (!this._stateLoaded) {
      this._stateLoaded = true;
      this.state = this._loadInitialState();
    }
    window.addEventListener("hashchange", this._onHashChange);
  }

  hostDisconnected() {
    window.removeEventListener("hashchange", this._onHashChange);
  }

  private _onHashChange = () => {
    this.state = this._loadInitialState();
    this.externalHashChange = true;
    this.host.requestUpdate();
  };

  private _loadInitialState(): AppState {
    const config = this._getConfig();
    const hash = window.location.hash;
    if (hash) {
      const decoded = decodeRatings(hash, config);
      // A hash that fails to parse (unknown version, truncated, garbage)
      // decodes to zero datasets. Fall through to localStorage/default
      // instead of handing back a blank state and silently discarding
      // whatever the user had saved.
      if (decoded.datasets.length > 0) {
        return decoded;
      }
    }

    const local = loadFromLocal();
    if (local && local.datasets && local.datasets.length > 0) {
      return local;
    }

    const versionConfig = config.versions[config.currentVersion];
    const defaultRatings: Record<string, number | null> = {};
    for (const id of versionConfig.attributeIds) {
      defaultRatings[id] = null;
    }
    return {
      datasets: [{ ratings: defaultRatings, color: config.datasetColors[0] }],
      theme: "system"
    };
  }

  update(newState: AppState) {
    this.state = newState;
    const hash = encodeRatings(this.state, this._getConfig());
    history.replaceState(null, "", hash);
    saveToLocal(this.state);
    this.host.requestUpdate();
  }
}
